import { useCallback, useEffect, useRef } from 'react';

import {
	canRegisterReads,
	FLUSH_INTERVAL_MS,
	hasDwelled,
	nextHighWater,
	shouldFlush,
	VISIBILITY_THRESHOLD,
} from '../lib/support-read-receipts';

/**
 * Reports which messages have actually been looked at.
 *
 * <p>A thin DOM wrapper around `support-read-receipts.ts` — every decision worth getting right
 * lives there and is unit-tested. This part owns only the observer, the listeners and the timer,
 * none of which jsdom can exercise.</p>
 *
 * <p>The strictness is evidentiary. Marking a thread read on open claims a sixty-message backlog
 * was read in 200ms, and in a dispute that claim is a record — a false one. A receipt here says
 * a human looked at this.</p>
 */
export const useReadReceipts = (
	threadActive: boolean,
	onReport: (upToSequence: number) => void
): { observe: (element: HTMLElement | null, sequence: number) => void } => {
	const visibleSince = useRef<Map<number, number>>(new Map());
	const observed = useRef<Map<Element, number>>(new Map());
	const observer = useRef<IntersectionObserver | null>(null);
	const reported = useRef(0);
	const lastFlush = useRef(0);
	const activeRef = useRef(threadActive);
	activeRef.current = threadActive;

	const onReportRef = useRef(onReport);
	onReportRef.current = onReport;

	useEffect(() => {
		// jsdom has no IntersectionObserver, and neither do very old WebViews. Degrading to
		// "never report" is correct: no receipt is better than a fabricated one.
		if (typeof IntersectionObserver === 'undefined') return;

		observer.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const sequence = observed.current.get(entry.target);
					if (sequence === undefined) return;

					if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
						// Only stamp the FIRST crossing — restamping on every callback would keep
						// resetting the dwell clock and nothing would ever qualify.
						if (!visibleSince.current.has(sequence)) visibleSince.current.set(sequence, Date.now());
					} else {
						visibleSince.current.delete(sequence);
					}
				});
			},
			{ threshold: [VISIBILITY_THRESHOLD] }
		);

		return () => {
			observer.current?.disconnect();
			observer.current = null;
		};
	}, []);

	useEffect(() => {
		const tick = window.setInterval(() => {
			const context = {
				documentVisible: typeof document === 'undefined' || document.visibilityState === 'visible',
				windowFocused: typeof document === 'undefined' || document.hasFocus(),
				threadActive: activeRef.current,
			};

			if (!canRegisterReads(context)) return;

			const now = Date.now();
			const dwelled: number[] = [];
			visibleSince.current.forEach((since, sequence) => {
				if (hasDwelled(since, now)) dwelled.push(sequence);
			});

			const mark = nextHighWater(dwelled, reported.current);
			if (mark === null) return;

			// Count is the number of NEW sequences, so a single message does not wait for a full
			// batch and a fast scroll does not fire per bubble.
			const pending = dwelled.filter((s) => s > reported.current).length;
			if (!shouldFlush(pending, now - lastFlush.current)) return;

			reported.current = mark;
			lastFlush.current = now;
			onReportRef.current(mark);
		}, FLUSH_INTERVAL_MS / 2);

		return () => window.clearInterval(tick);
	}, []);

	/** Ref callback for a bubble. Passing null unobserves — call it on unmount. */
	const observe = useCallback((element: HTMLElement | null, sequence: number) => {
		if (!observer.current) return;

		if (!element) {
			observed.current.forEach((seq, node) => {
				if (seq !== sequence) return;
				observer.current?.unobserve(node);
				observed.current.delete(node);
			});
			visibleSince.current.delete(sequence);
			return;
		}

		observed.current.set(element, sequence);
		observer.current.observe(element);
	}, []);

	return { observe };
};
